import React, { useState, useEffect } from 'react';

interface FileViewerProps {
    fileUrl: string;
    fileName: string;
    fileType?: string; // mime type if available, or extension
    onClose: () => void;
    onSave?: (content: string) => void; // For text editing
    readOnly?: boolean;
    theme?: 'light' | 'dark';
}

const FileViewer: React.FC<FileViewerProps> = ({
    fileUrl,
    fileName,
    fileType,
    onClose,
    onSave,
    readOnly = false,
    theme = 'dark'
}) => {
    const isDark = theme === 'dark';
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const extension = fileName.split('.').pop()?.toLowerCase();

    // Determine viewer type
    const getViewerType = () => {
        if (['pdf'].includes(extension || '')) return 'pdf';
        if (['txt', 'js', 'css', 'html', 'json', 'md', 'ts', 'tsx', 'log', 'xml'].includes(extension || '')) return 'text';
        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension || '')) return 'office';
        if (['mp4', 'webm', 'ogg'].includes(extension || '')) return 'video';
        if (['mp3', 'wav'].includes(extension || '')) return 'audio';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) return 'image';
        return 'unknown';
    };

    const viewerType = getViewerType();

    // Fetch text content
    useEffect(() => {
        if (viewerType === 'text') {
            setLoading(true);
            fetch(fileUrl)
                .then(res => res.text())
                .then(text => {
                    setContent(text);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load text file:', err);
                    setError('Failed to load file content');
                    setLoading(false);
                });
        }
    }, [fileUrl, viewerType]);

    // Handle Save Text
    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                await onSave(content);
                setIsSaving(false);
            } catch (err) {
                console.error('Failed to save:', err);
                setIsSaving(false);
            }
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className={`w-full max-w-6xl h-[90vh] rounded-2xl overflow-hidden flex flex-col ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200'}`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
                            <span className="material-icons text-indigo-500">
                                {viewerType === 'pdf' ? 'picture_as_pdf' :
                                    viewerType === 'text' ? 'description' :
                                        viewerType === 'office' ? 'table_view' :
                                            viewerType === 'video' ? 'movie' : 'insert_drive_file'}
                            </span>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold truncate max-w-md">{fileName}</h2>
                            <p className="text-xs opacity-60 uppercase">{extension} File</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {viewerType === 'text' && !readOnly && onSave && (
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                <span className={`material-icons text-sm ${isSaving ? 'animate-spin' : ''}`}>
                                    {isSaving ? 'sync' : 'save'}
                                </span>
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                        <a
                            href={fileUrl}
                            download={fileName}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                            title="Download"
                        >
                            <span className="material-icons">download</span>
                        </a>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600 hover:text-red-400' : 'bg-gray-100 hover:bg-gray-200 hover:text-red-500'}`}
                        >
                            <span className="material-icons">close</span>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 bg-neutral-900 overflow-hidden relative flex flex-col">
                    {viewerType === 'pdf' && (
                        <iframe
                            src={fileUrl}
                            className="w-full h-full border-0"
                            title="PDF Viewer"
                        />
                    )}

                    {viewerType === 'office' && (
                        <iframe
                            src={`https://docs.google.com/gview?url=${encodeURIComponent(window.location.origin + fileUrl)}&embedded=true`}
                            className="w-full h-full border-0 bg-white"
                            title="Office Viewer"
                        />
                    )}

                    {viewerType === 'text' && (
                        <div className="flex-1 relative">
                            {loading ? (
                                <div className="absolute inset-0 flex items-center justify-center text-white">
                                    <span className="material-icons animate-spin text-4xl">sync</span>
                                </div>
                            ) : error ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-red-400 gap-2">
                                    <span className="material-icons text-4xl">error_outline</span>
                                    <p>{error}</p>
                                </div>
                            ) : (
                                <textarea
                                    value={content}
                                    onChange={(e) => !readOnly && setContent(e.target.value)}
                                    readOnly={readOnly}
                                    spellCheck={false}
                                    className={`w-full h-full p-4 font-mono text-sm resize-none outline-none ${isDark
                                            ? 'bg-[#1e1e1e] text-[#d4d4d4]'
                                            : 'bg-white text-gray-800'
                                        }`}
                                    style={{ lineHeight: '1.5', tabSize: 4 }}
                                />
                            )}
                        </div>
                    )}

                    {viewerType === 'video' && (
                        <div className="flex items-center justify-center h-full bg-black">
                            <video src={fileUrl} controls className="max-w-full max-h-full" />
                        </div>
                    )}

                    {viewerType === 'audio' && (
                        <div className="flex items-center justify-center h-full bg-slate-900">
                            <audio src={fileUrl} controls className="w-96" />
                        </div>
                    )}

                    {viewerType === 'image' && (
                        <div className="flex items-center justify-center h-full bg-black/50 overflow-auto">
                            <img src={fileUrl} alt={fileName} className="max-w-full max-h-full object-contain shadow-2xl" />
                        </div>
                    )}

                    {viewerType === 'unknown' && (
                        <div className="flex flex-col items-center justify-center h-full text-white gap-4">
                            <span className="material-icons text-6xl opacity-50">question_mark</span>
                            <div className="text-center">
                                <h3 className="text-xl font-semibold mb-2">Preview not available</h3>
                                <p className="opacity-60 mb-6">This file type cannot be previewed.</p>
                                <a
                                    href={fileUrl}
                                    download={fileName}
                                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium inline-flex items-center gap-2"
                                >
                                    <span className="material-icons">download</span>
                                    Download to view
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FileViewer;
